import { CodeBlock, CopyButton, PageHero, Section } from "../components/primitives.jsx";

const handoff = `Case: resource://case-studies/ai-built-billing
Requirements: BR-BILLING-0041, BR-BILLING-0042, BR-BILLING-0043
Patterns: boundary leak, requirement lens, observable failure contract, contract-first port
Evidence: policy table tests, provider timeout retry, notifier contract, one smoke flow
Residual risk: payment provider sandbox behavior still needs release-environment confirmation`;

const diagram = `before

HTTP handler
  -> ORM user lookup
  -> billing if/else
  -> Stripe charge
  -> email receipt
  -> JSON response

after

Controller
  -> InvoiceCommand
  -> BillingApplicationService
      -> BillingPolicy
      -> InvoiceRepository port     [pending invoice + status]
      -> PaymentGateway port        [idempotency key]
      -> ReceiptNotifier port       [after confirmed charge]
  -> Presenter`;

const risks = [
  ["crit", "Risk 01", "Boundary leak", "Billing policy depends on Express request shape, ORM records, and Stripe response fields."],
  ["accent", "Risk 02", "Test hourglass", "Only happy-path unit tests and slow browser payment flows protect revenue behavior."],
  ["muted", "Risk 03", "Requirement drift", "Free plan cannot invoice over 100 exists in chat, code comments, and one brittle test."]
];

const evidence = [
  ["crit", "Before", "2 unit tests, 6 browser flows", "Slow payment flows carry policy, provider, and notification confidence at once."],
  ["accent", "Target", "Policy + port contracts", "Move confidence to table tests, provider contract tests, and notifier adapter tests."],
  ["muted", "Keep", "1 smoke payment flow", "Use one end-to-end path to prove wiring, not every billing rule."]
];

const playbook = [
  ["01", "Freeze behavior with requirement IDs.", "Write three billing invariants and attach them to existing tests.", "Done when: each invariant has a BR ID and at least one evidence target."],
  ["02", "Extract policy before adapters.", "Move pure decisions first, then wrap payment and notification effects.", "Done when: policy tests run without HTTP, ORM, Stripe, or email setup."],
  ["03", "Add boundary evidence.", "Run policy tests, adapter contract tests, and one end-to-end smoke flow.", "Done when: results include command output, telemetry name, and residual risk."],
  ["04", "Publish the packet.", "Save the before/after map, side effects, and residual risks in the spec.", "Done when: the handoff references resource://case-studies/ai-built-billing."]
];

export function CaseStudyPage() {
  return (
    <main>
      <PageHero eyebrow="Case study" title="AI-built billing backend, rescued before scale.">
        <p>
          A founder ships billing fast with agents. The first version works, but domain rules,
          payment provider calls, database writes, and email side effects all live in the same
          handlers.
        </p>
        <code className="page-hero__uri">resource://case-studies/ai-built-billing</code>
      </PageHero>

      <section className="section">
        <div className="container split">
          <div>
            <span className="lbl">Scenario</span>
            <h2 className="section__title">The trigger is a real billing failure mode.</h2>
            <p className="split__copy">
              A usage-based SaaS has free, pro, and enterprise plans. A free-plan user crosses the
              invoice threshold, the payment provider times out, and a retry path risks charging
              twice while the receipt email can be sent before the charge is confirmed.
            </p>
          </div>
          <div className="cell cell--sunken ledger">
            <div className="cell__chrome"><span>Facts on the table</span><span>billing</span></div>
            <div className="ledger__row ledger__head"><span>fact</span><span>risk</span><span>proof</span></div>
            <div className="ledger__row"><span>Free plan limit: 100 USD</span><strong className="tone-crit">policy</strong><span>table tests</span></div>
            <div className="ledger__row"><span>Provider timeout on retry</span><strong className="tone-accent">charge</strong><span>idempotency key</span></div>
            <div className="ledger__row"><span>Receipt after payment only</span><strong className="tone-muted">ordering</strong><span>audit log</span></div>
          </div>
        </div>
      </section>

      <Section eyebrow="Diagnosis" title="The bad architecture is not messy. It is unprotected.">
        <div className="case-grid">
          {risks.map(([tone, code, title, copy]) => (
            <article key={title} className={`cell case-card case-card--${tone}`}>
              <div className="cell__chrome"><span>{code}</span></div>
              <h3 className="case-card__title">{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </Section>

      <section className="section">
        <div className="container split">
          <div>
            <span className="lbl">Architecture map</span>
            <h2 className="section__title">Refactor toward ports and evidence gates.</h2>
            <p className="split__copy">
              The case study does not prescribe a framework. It preserves the invariant: domain
              policy owns decisions, adapters own effects, and verification proves both paths.
            </p>
            <div className="action-map">
              <div><strong>Risk to move</strong><span>Boundary leak → policy and ports.</span></div>
              <div><strong>Risk to proof</strong><span>Test hourglass → policy, contract, adapter, and one smoke flow.</span></div>
              <div><strong>Risk to memory</strong><span>Requirement drift → BR IDs and an agent handoff packet.</span></div>
            </div>
          </div>
          <figure className="case-diagram">
            <CodeBlock label="Before / after">{diagram}</CodeBlock>
            <figcaption className="lbl">
              The refactor stores invoice state before charging, records the provider result with
              an idempotency key, and sends receipts only after confirmed payment.
            </figcaption>
          </figure>
        </div>
      </section>

      <Section id="requirements-map" eyebrow="Requirement map" title="Each refactor action protects a named need.">
        <div className="table-shell">
          <table>
            <thead>
              <tr><th>Need</th><th>Risk and pattern</th><th>Refactor action</th><th>Acceptance evidence</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><code>BR-BILLING-0041</code><br />Free plan limit</td>
                <td>Requirement lens + boundary leak</td>
                <td>Move decision into BillingPolicy</td>
                <td>Policy table tests cover free/pro/enterprise limits; BillingPolicy has no HTTP, ORM, or Stripe imports.</td>
              </tr>
              <tr>
                <td><code>BR-BILLING-0042</code><br />Idempotent invoice charge</td>
                <td>Observable failure contract</td>
                <td>Add idempotency key and payment status states</td>
                <td>Retry after simulated provider timeout records one payment attempt and emits billing.charge.failure telemetry.</td>
              </tr>
              <tr>
                <td><code>BR-BILLING-0043</code><br />Receipt after confirmed charge</td>
                <td>Contract-first port + failure contract</td>
                <td>Make receipt notifier an adapter</td>
                <td>Notifier contract proves no receipt before confirmed payment; audit log ties invoice, attempt, and notification result.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section eyebrow="Evidence inventory" title="Rebalance the test hourglass." compact>
        <div className="case-grid">
          {evidence.map(([tone, code, title, copy]) => (
            <article key={title} className={`cell case-card case-card--${tone}`}>
              <div className="cell__chrome"><span>{code}</span></div>
              <h3 className="case-card__title">{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </Section>

      <section className="section">
        <div className="container split">
          <div>
            <span className="lbl">Review playbook</span>
            <h2 className="section__title">Actionable sequence for developers and agents.</h2>
            <p className="split__copy">
              This is the page a founder can hand to an AI agent: identify the pattern, change the
              smallest boundary, and prove the behavior with recorded evidence.
            </p>
            <CopyButton text={handoff} primary>Copy agent handoff</CopyButton>
          </div>
          <ol className="timeline">
            {playbook.map(([step, title, copy, done]) => (
              <li key={step}>
                <span className="timeline__num">{step}</span>
                <div>
                  <strong>{title}</strong>
                  <p>{copy}</p>
                  <small className="lbl">{done}</small>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </main>
  );
}
